import React, { useMemo, useState } from 'react';

import { useManagementPackContext } from '../ManagementPackContext';
import { SNAPSHOT_COMPLETED } from '../managementPackUtils';
import StatusOverrideModal from '../StatusOverrideModal';
import StatusPill from '../StatusPill';
import TrendArrow from '../TrendArrow';

import TextButton from 'CommonComponents/TextButton';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';

const SEARCH_THRESHOLD = 10;

const GROUPS = [
    { key: 'areas', title: () => $t('Areas') },
    { key: 'departments', title: () => $t('Departments') },
    { key: 'plans', title: () => $t('Plans') },
];

/**
 * Why the statuses cannot be set by hand, or null when they can.
 */
const readOnlyReason = (activePack, statuses) => {
    if (!activePack) {
        return $t('Generate the pack to set statuses manually.');
    }

    if (activePack.isIssued) {
        return $t('This pack is issued, so its statuses cannot be changed.');
    }

    if (activePack.snapshotStatus !== SNAPSHOT_COMPLETED) {
        return $t('Statuses can be set manually once the pack data has been captured.');
    }

    if (!statuses?.canOverride) {
        return $t('You do not have permission to set statuses manually.');
    }

    return null;
};

const StatusRow = ({ row, readOnly, onSet, onRemove }) => (
    <li className="tw-flex tw-flex-col tw-gap-1.5 tw-py-3 tw-border-b tw-border-charcoal-15 last:tw-border-b-0">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <span className="tw-text-sm tw-font-semibold tw-text-charcoal tw-min-w-0 tw-truncate" title={row.label}>
                {row.label}
            </span>
            <span className="tw-flex tw-items-center tw-gap-1.5 tw-shrink-0">
                <StatusPill status={row.status} />
                <TrendArrow trend={row.trend} className="tw-text-xs" />
                {row.manual && (
                    <span className="tw-text-charcoal-60 tw-text-2xs" title={$t('Set manually')}>
                        {$t('manual')}
                    </span>
                )}
            </span>
        </div>
        {row.manual && (
            <p className="tw-m-0 tw-text-xs tw-text-charcoal-75">
                {$t('Rules give')} <StatusPill status={row.ruleStatus} /> {row.note ? `· ${row.note}` : ''}
            </p>
        )}
        {!readOnly && (
            <div className="tw-flex tw-gap-3">
                <TextButton onClick={() => onSet(row)}>{row.manual ? $t('Change manual status') : $t('Set manually')}</TextButton>
                {row.manual && (
                    <TextButton variant="danger" onClick={() => onRemove(row)}>
                        {$t('Remove manual status')}
                    </TextButton>
                )}
            </div>
        )}
    </li>
);

const StatusGroup = ({ title, rows, readOnly, onSet, onRemove }) => {
    const [search, setSearch] = useState('');

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();

        return term ? rows.filter((row) => row.label.toLowerCase().includes(term)) : rows;
    }, [rows, search]);

    if (!rows.length) {
        return null;
    }

    return (
        <section className="tw-flex tw-flex-col tw-gap-2" aria-label={title}>
            <h3 className="tw-text-2xs tw-font-semibold tw-uppercase tw-text-blue tw-m-0">{title}</h3>
            {rows.length > SEARCH_THRESHOLD && (
                <input
                    type="search"
                    className="text-input tw-border-0"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={$t('Search')}
                    aria-label={$t('Search {{group}}', { group: title })}
                />
            )}
            <ul className="tw-list-none tw-m-0 tw-p-0">
                {visible.map((row) => (
                    <StatusRow key={`${row.subjectType}:${row.subjectKey}`} row={row} readOnly={readOnly} onSet={onSet} onRemove={onRemove} />
                ))}
            </ul>
        </section>
    );
};

/**
 * Every status the preview applied, grouped into areas, departments and plans,
 * with the rule status, the final status, the manual marker and the trend.
 * A draft's statuses can be set by hand with a note, or have that removed.
 */
const StatusesTab = () => {
    const { preview, activePack, saveStatusOverride, removeStatusOverride } = useManagementPackContext();
    const modal = useModal();
    const confirm = useConfirmationModal();
    const [subject, setSubject] = useState(null);

    const statuses = preview?.statuses;
    const reason = readOnlyReason(activePack, statuses);

    const openModal = (row) => {
        setSubject({ ...row });
        modal.show();
    };

    const remove = (row) =>
        confirm({
            title: $t('Remove the manual status?'),
            message: $t('{{label}} goes back to the status the rules give.', { label: row.label }),
            actions: [
                { confirmed: false, label: $t('Cancel'), variant: 'secondary' },
                {
                    confirmed: true,
                    label: $t('Remove'),
                    variant: 'danger',
                    onClick: () => removeStatusOverride(row.subjectType, row.subjectKey),
                },
            ],
        });

    if (!statuses) {
        return <p className="tw-text-sm tw-text-charcoal-75 tw-py-4 tw-m-0">{$t('The statuses appear with the preview.')}</p>;
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-5 tw-py-4">
            {!!reason && <p className="tw-text-sm tw-text-charcoal-75 tw-m-0">{reason}</p>}
            {GROUPS.map((group) => (
                <StatusGroup key={group.key} title={group.title()} rows={statuses[group.key] || []} readOnly={!!reason} onSet={openModal} onRemove={remove} />
            ))}
            <StatusOverrideModal modal={modal} subject={subject} onSave={(row, values) => saveStatusOverride(row.subjectType, row.subjectKey, values)} />
        </div>
    );
};

export default StatusesTab;
